import React, {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaCheck,
  FaChalkboardTeacher,
  FaPlus,
  FaRandom,
  FaSyncAlt,
  FaTimes,
  FaUserGraduate,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const MdlSelectSingleStudent = React.lazy(() =>
  import("../dialog/MdlSelectSingleStudent")
);

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

//Created By Cursor
function classSectionLabel(row) {
  if (!row) return "—";
  const sectionId = String(row.section_id || "");
  if (!row.section_name || sectionId === "No") {
    return row.class_name || "—";
  }
  return `${row.class_name || ""}:${row.section_name}`;
}

//Created By Cursor — flatten getClass.php class + nested sections
function flattenClassSections(classData = []) {
  const out = [];
  classData.forEach((item) => {
    if (!item) return;
    if (item.section === "No") {
      out.push({
        key: `${item.class_id}-No-${item.board_id}-${item.medium_id}`,
        class_id: item.class_id,
        class_name: item.class_name,
        section_id: "No",
        section_name: "",
        board_id: item.board_id,
        medium_id: item.medium_id,
        board_name: item.board_name,
        medium_name: item.medium_name,
      });
      return;
    }
    const sections = Array.isArray(item.section) ? item.section : [];
    sections.forEach((sec) => {
      out.push({
        key: `${sec.class_id || item.class_id}-${sec.section_id}-${item.board_id}-${item.medium_id}`,
        class_id: sec.class_id || item.class_id,
        class_name: sec.class_name || item.class_name,
        section_id: sec.section_id,
        section_name: sec.section_name,
        board_id: item.board_id,
        medium_id: item.medium_id,
        board_name: item.board_name,
        medium_name: item.medium_name,
      });
    });
  });
  return out;
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="sj-avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="sj-avatar__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

//Created By Cursor
export default function SessionJump() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [student, setStudent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [addSessionId, setAddSessionId] = useState("");
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClassKey, setSelectedClassKey] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [saving, setSaving] = useState(false);

  //Created By Cursor
  const enrollmentNo = useMemo(
    () => String(student?.student_enrollment_no || "").trim(),
    [student]
  );

  //Created By Cursor
  const selectedClass = useMemo(
    () => classOptions.find((c) => c.key === selectedClassKey) || null,
    [classOptions, selectedClassKey]
  );

  //Created By Cursor
  const loadSessions = useCallback(async (enrollment) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !enrollment) {
      setSessions([]);
      return;
    }
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API_BASE()}getStudentSessionJump.php`, {
        params: {
          school_code: schoolCode,
          student_enrollment_no: enrollment,
        },
      });
      if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
        setSessions(res.data.data);
      } else {
        setSessions([]);
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Failed to load sessions."
        );
      }
    } catch (err) {
      console.error("[SessionJump] load sessions:", err);
      setSessions([]);
      myToast.notify_warning("Failed to load sessions.");
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  //Created By Cursor
  const loadClassesForSession = useCallback(async (sessionId) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId) {
      setClassOptions([]);
      return;
    }
    setLoadingClasses(true);
    setSelectedClassKey("");
    setClassOptions([]);
    try {
      const res = await axios.get(`${API_BASE()}getClass.php`, {
        params: { school_code: schoolCode, session_id: sessionId },
      });
      if (res.data?.api_status === "OK") {
        const flat = flattenClassSections(res.data.data?.class || []);
        setClassOptions(flat);
      } else {
        myToast.notify_warning(
          res.data?.api_remark || "Failed to load classes."
        );
      }
    } catch (err) {
      console.error("[SessionJump] load classes:", err);
      myToast.notify_warning("Failed to load classes.");
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  //Created By Cursor
  const handleSelectedStudent = useCallback(
    (studentId, selectedItem) => {
      const next = selectedItem || { student_enrollment_no: studentId };
      setStudent(next);
      setAddSessionId("");
      setClassOptions([]);
      setSelectedClassKey("");
      setRollNo(String(next.student_roll_no || ""));
      setPickerOpen(false);
      loadSessions(String(next.student_enrollment_no || studentId || "").trim());
    },
    [loadSessions]
  );

  //Created By Cursor
  const openAddPanel = useCallback(
    (sessionId) => {
      setAddSessionId(String(sessionId));
      setSelectedClassKey("");
      setRollNo(String(student?.student_roll_no || ""));
      loadClassesForSession(sessionId);
    },
    [loadClassesForSession, student]
  );

  //Created By Cursor
  const closeAddPanel = useCallback(() => {
    if (saving) return;
    setAddSessionId("");
    setClassOptions([]);
    setSelectedClassKey("");
  }, [saving]);

  //Created By Cursor
  const handleAddToSession = useCallback(async () => {
    if (!enrollmentNo || !addSessionId || !selectedClass || saving) return;
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    setSaving(true);
    try {
      const body = new URLSearchParams();
      body.set("school_code", schoolCode);
      body.set("student_enrollment_no", enrollmentNo);
      body.set("session_id", addSessionId);
      body.set("class_id", String(selectedClass.class_id || ""));
      body.set("section_id", String(selectedClass.section_id || ""));
      body.set("board_id", String(selectedClass.board_id || ""));
      body.set("medium_id", String(selectedClass.medium_id || ""));
      body.set("student_roll_no", String(rollNo || "0"));

      const res = await axios.post(`${API_BASE()}addStudentToSession.php`, body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Student added to session.");
        setAddSessionId("");
        setClassOptions([]);
        setSelectedClassKey("");
        await loadSessions(enrollmentNo);
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Failed to add student."
        );
      }
    } catch (err) {
      console.error("[SessionJump] add error:", err);
      myToast.notify_warning(
        err?.response?.data?.api_remark ||
          err?.response?.data?.message ||
          "Failed to add student to session."
      );
    } finally {
      setSaving(false);
    }
  }, [
    enrollmentNo,
    addSessionId,
    selectedClass,
    saving,
    rollNo,
    loadSessions,
  ]);

  //Created By Cursor
  const sessionStats = useMemo(() => {
    let registered = 0;
    sessions.forEach((row) => {
      if (Number(row.is_registered) === 1 || row.is_registered === true) {
        registered += 1;
      }
    });
    return {
      total: sessions.length,
      registered,
      missing: Math.max(0, sessions.length - registered),
    };
  }, [sessions]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sj-page"
      style={{ background: "#f5f7fb", fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="sj-crumb"
                onClick={() => history.push("/AdminMain/AdmissionDashboard/1")}
              >
                Admission
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Session Jump</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaRandom />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Session Jump</h1>
              <p className="sm-hero__sub">
                Register a student into sessions where they are missing
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="sj-btn sj-btn--primary"
              onClick={() => setPickerOpen(true)}
            >
              <FaUserGraduate />
              <span>{student ? "Change student" : "Select student"}</span>
            </button>
            {student ? (
              <button
                type="button"
                className={`sj-icon-btn${loadingSessions ? " is-spinning" : ""}`}
                onClick={() => loadSessions(enrollmentNo)}
                disabled={loadingSessions || saving}
                aria-label="Refresh sessions"
                title="Refresh"
              >
                <FaSyncAlt aria-hidden />
              </button>
            ) : null}
          </div>
        </header>

        <div className="sj-content">
          {!student ? (
            <div className="sj-empty">
              <div className="sj-empty__icon" aria-hidden>
                <FaUserGraduate />
              </div>
              <p className="sj-empty__title">Select a student</p>
              <p className="sj-empty__hint">
                Pick a student to see every school session and jump them into
                any session they are not registered in.
              </p>
              <button
                type="button"
                className="sj-btn sj-btn--primary"
                onClick={() => setPickerOpen(true)}
              >
                <FaUserGraduate />
                <span>Select student</span>
              </button>
            </div>
          ) : (
            <>
              <section className="sj-student-card" aria-label="Selected student">
                <StudentAvatar
                  name={student.stu_name}
                  imagePath={student.student_img_path}
                />
                <div className="sj-student-card__body min-w-0">
                  <p className="sj-student-card__eyebrow">Selected student</p>
                  <strong className="sj-student-card__name">
                    {student.stu_name || "Student"}
                  </strong>
                  <div className="sj-student-card__meta">
                    <span className="sj-chip">
                      Enroll {enrollmentNo || "—"}
                    </span>
                    {student.sr_admission_no ? (
                      <span className="sj-chip">SRN {student.sr_admission_no}</span>
                    ) : null}
                    {student.father_name ? (
                      <span className="sj-chip">
                        Father {student.father_name}
                      </span>
                    ) : null}
                  </div>
                </div>
                {!loadingSessions && sessions.length > 0 ? (
                  <div className="sj-student-card__stats" aria-label="Session summary">
                    <div className="sj-stat">
                      <strong>{sessionStats.total}</strong>
                      <span>Sessions</span>
                    </div>
                    <div className="sj-stat sj-stat--ok">
                      <strong>{sessionStats.registered}</strong>
                      <span>Registered</span>
                    </div>
                    <div className="sj-stat sj-stat--miss">
                      <strong>{sessionStats.missing}</strong>
                      <span>Missing</span>
                    </div>
                  </div>
                ) : null}
              </section>

              {loadingSessions ? (
                <div className="sj-loading">
                  <MyLoader />
                  <p>Loading sessions…</p>
                </div>
              ) : sessions.length === 0 ? (
                <div className="sj-empty sj-empty--compact">
                  <div className="sj-empty__icon" aria-hidden>
                    <FaCalendarAlt />
                  </div>
                  <p className="sj-empty__title">No sessions found</p>
                  <p className="sj-empty__hint">
                    No active sessions are available for this school.
                  </p>
                </div>
              ) : (
                <section className="sj-session-section" aria-label="Sessions">
                  <div className="sj-section-head">
                    <h2 className="sj-section-head__title">Sessions</h2>
                    <p className="sj-section-head__sub">
                      Registered sessions show class details. Use Add in session
                      where the student is missing.
                    </p>
                  </div>
                  <div className="sj-session-list">
                    {sessions.map((row, index) => {
                      const sid = String(row.session_id);
                      const registered =
                        Number(row.is_registered) === 1 ||
                        row.is_registered === true;
                      const isAdding = addSessionId === sid;
                      return (
                        <article
                          key={sid}
                          className={`sj-session-row${
                            registered ? " is-registered" : ""
                          }${isAdding ? " is-adding" : ""}`}
                          style={{ "--sj-i": index }}
                        >
                          <div className="sj-session-row__main">
                            <div className="sj-session-row__identity">
                              <span
                                className={`sj-session-row__icon${
                                  registered ? " is-ok" : ""
                                }`}
                                aria-hidden
                              >
                                {registered ? <FaCheck /> : <FaCalendarAlt />}
                              </span>
                              <div className="min-w-0">
                                <strong className="sj-session-row__name">
                                  {row.session_name || sid}
                                </strong>
                                <div className="sj-session-row__dates">
                                  <FaCalendarAlt aria-hidden />
                                  <span>
                                    {row.start_date || "—"} →{" "}
                                    {row.end_date || "—"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {registered ? (
                              <div className="sj-session-row__status">
                                <span className="sj-badge sj-badge--ok">
                                  <FaCheck /> Registered
                                </span>
                                <span className="sj-session-row__class">
                                  <FaChalkboardTeacher aria-hidden />
                                  {classSectionLabel(row)}
                                  {row.student_roll_no
                                    ? ` · Roll ${row.student_roll_no}`
                                    : ""}
                                </span>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={`sj-btn${
                                  isAdding ? " sj-btn--ghost" : " sj-btn--accent"
                                }`}
                                disabled={saving}
                                onClick={() =>
                                  isAdding ? closeAddPanel() : openAddPanel(sid)
                                }
                              >
                                {isAdding ? <FaTimes /> : <FaPlus />}
                                <span>
                                  {isAdding ? "Cancel" : "Add in session"}
                                </span>
                              </button>
                            )}
                          </div>

                          {isAdding && !registered ? (
                            <div className="sj-add-panel">
                              <div className="sj-add-panel__head">
                                <strong>Add to {row.session_name || sid}</strong>
                                <span>
                                  Choose class/section for this session
                                </span>
                              </div>
                              {loadingClasses ? (
                                <div className="sj-add-panel__loading">
                                  <MyLoader />
                                  <p>Loading classes…</p>
                                </div>
                              ) : classOptions.length === 0 ? (
                                <p className="sj-add-panel__hint">
                                  No classes found for this session.
                                </p>
                              ) : (
                                <div className="sj-add-panel__form">
                                  <label className="sj-field sj-field--grow">
                                    <span>Class / section</span>
                                    <select
                                      value={selectedClassKey}
                                      onChange={(e) =>
                                        setSelectedClassKey(e.target.value)
                                      }
                                      disabled={saving}
                                    >
                                      <option value="">Select class</option>
                                      {classOptions.map((opt) => (
                                        <option key={opt.key} value={opt.key}>
                                          {classSectionLabel(opt)}
                                          {opt.board_name || opt.medium_name
                                            ? ` (${[
                                                opt.board_name,
                                                opt.medium_name,
                                              ]
                                                .filter(Boolean)
                                                .join(" · ")})`
                                            : ""}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="sj-field sj-field--roll">
                                    <span>Roll no</span>
                                    <input
                                      type="text"
                                      value={rollNo}
                                      onChange={(e) => setRollNo(e.target.value)}
                                      disabled={saving}
                                      placeholder="0"
                                    />
                                  </label>
                                  <div className="sj-add-panel__actions">
                                    <button
                                      type="button"
                                      className="sj-btn sj-btn--ghost"
                                      disabled={saving}
                                      onClick={closeAddPanel}
                                    >
                                      <FaTimes />
                                      <span>Cancel</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="sj-btn sj-btn--primary"
                                      disabled={saving || !selectedClass}
                                      onClick={handleAddToSession}
                                    >
                                      <FaPlus />
                                      <span>
                                        {saving ? "Adding…" : "Confirm add"}
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      {pickerOpen ? (
        <Suspense fallback={<MyLoader />}>
          <MdlSelectSingleStudent
            action={() => setPickerOpen(false)}
            action2={handleSelectedStudent}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
